import React, { useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Statistic,
  Table,
  Tag,
  Progress,
  Button,
  Space,
} from "antd";
import {
  TeamOutlined,
  MedicineBoxOutlined,
  UserOutlined,
  FileTextOutlined,
  AlertOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../store";
import {
  getStockStatsStart,
  getStockAlertsStart,
} from "../../store/slices/stockSlice";
import {
  getSuppliersStart,
  getSupplierStatsStart,
} from "../../store/slices/supplierSlice";
import { fetchBillStatsStart } from "../../store/slices/billingSlice";
import PageHeader from "../../components/PageHeader";

const Dashboard: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { stats: stockStats, alerts } = useSelector(
    (state: RootState) => state.stock
  );
  const { suppliers, stats: supplierStats } = useSelector(
    (state: RootState) => state.suppliers
  );
  const { billStats: billingStats } = useSelector(
    (state: RootState) => state.billing
  );

  useEffect(() => {
    dispatch(getStockStatsStart());
    dispatch(getStockAlertsStart());
    dispatch(getSuppliersStart({ page: 1, limit: 5 }));
    dispatch(getSupplierStatsStart());
    dispatch(fetchBillStatsStart());
  }, [dispatch]);

  const [payables, setPayables] = React.useState(0);
  React.useEffect(() => {
    import("../../services/purchaseService")
      .then((m) => m.default.getStats())
      .then((res) => setPayables(res.data.data?.totalPayables || 0))
      .catch(() => undefined);
  }, []);

  const recentAlertsColumns = [
    {
      title: "Item",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        const color =
          status === "expired"
            ? "red"
            : status === "expiring"
            ? "orange"
            : "gold";
        return (
          <Tag color={color}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Tag>
        );
      },
    },
    {
      title: "Quantity",
      key: "quantity",
      render: (record: any) =>
        `${record.quantity?.current || 0} ${record.quantity?.unit || "units"}`,
    },
  ];

  const getAlertData = () => {
    if (!alerts) return [];
    const alertItems = [
      ...alerts.lowStock
        .slice(0, 3)
        .map((item: any) => ({ ...item, status: "low stock" })),
      ...alerts.expiring
        .slice(0, 2)
        .map((item: any) => ({ ...item, status: "expiring" })),
      ...alerts.expired
        .slice(0, 2)
        .map((item: any) => ({ ...item, status: "expired" })),
    ];
    return alertItems.slice(0, 5);
  };

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Stock, sales, and supplier snapshot"
        extra={
          <Space size={8}>
            <Button size="small" onClick={() => navigate("/billing")}>
              New bill
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={() => navigate("/purchases/new")}
            >
              New purchase
            </Button>
          </Space>
        }
      />

      <Row gutter={[8, 8]}>
        <Col xs={12} sm={12} lg={6}>
          <Card className="stat-card" size="small">
            <Statistic
              title="Suppliers"
              value={supplierStats?.totalSuppliers || suppliers.length || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: "#0d7a6f" }}
            />
            <div style={{ marginTop: 4 }}>
              <Progress
                percent={
                  supplierStats?.activeSuppliers
                    ? Math.round(
                        (supplierStats.activeSuppliers /
                          supplierStats.totalSuppliers) *
                          100
                      )
                    : 0
                }
                size="small"
                showInfo={false}
                strokeColor="#0d7a6f"
              />
              <small style={{ color: "#64748b" }}>
                {supplierStats?.activeSuppliers || 0} active
              </small>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card className="stat-card" size="small">
            <Statistic
              title="Stock items"
              value={stockStats?.totalItems || 0}
              prefix={<MedicineBoxOutlined />}
              valueStyle={{ color: "#0369a1" }}
            />
            <div style={{ marginTop: 4 }}>
              <small style={{ color: "#64748b" }}>
                {stockStats?.lowStockItems || 0} need attention
              </small>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card className="stat-card" size="small">
            <Statistic
              title="Patients"
              value={billingStats?.totalPatients || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: "#b45309" }}
            />
            <div style={{ marginTop: 4 }}>
              <small style={{ color: "#64748b" }}>Registered</small>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card className="stat-card" size="small">
            <Statistic
              title="Monthly revenue"
              value={billingStats?.monthlyRevenue || 0}
              prefix={<DollarOutlined />}
              precision={0}
              valueStyle={{ color: "#0f766e" }}
            />
            <div style={{ marginTop: 4 }}>
              <small style={{ color: "#64748b" }}>
                {billingStats?.monthlyBills || 0} bills this month
              </small>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
        <Col xs={12} sm={6}>
          <Card className="stat-card" size="small">
            <Statistic
              title="Stock value"
              value={stockStats?.stockValue?.totalSellingValue || 0}
              prefix="₹"
              precision={0}
              valueStyle={{ color: "#0d7a6f", fontSize: 18 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="stat-card" size="small">
            <Statistic
              title="Payables"
              value={payables}
              prefix="₹"
              precision={0}
              valueStyle={{ color: "#c2410c", fontSize: 18 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="stat-card" size="small">
            <Statistic
              title="Low stock"
              value={alerts?.summary?.lowStockCount || 0}
              prefix={<AlertOutlined />}
              valueStyle={{ color: "#ca8a04", fontSize: 18 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="stat-card" size="small">
            <Statistic
              title="Pending payments"
              value={billingStats?.pendingPayments || 0}
              prefix="₹"
              precision={0}
              valueStyle={{ color: "#b91c1c", fontSize: 18 }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[8, 8]} style={{ marginTop: 10 }}>
        <Col xs={24} lg={12}>
          <div className="page-panel">
            <div className="page-header" style={{ marginBottom: 8 }}>
              <strong style={{ fontSize: 13 }}>Stock alerts</strong>
            </div>
            {getAlertData().length > 0 ? (
              <Table
                className="dense-table"
                columns={recentAlertsColumns}
                dataSource={getAlertData()}
                rowKey={(record) => `${record._id}-${record.status}`}
                pagination={false}
                size="small"
              />
            ) : (
              <p style={{ textAlign: "center", color: "#94a3b8", margin: "12px 0" }}>
                No stock alerts
              </p>
            )}
          </div>
        </Col>
        <Col xs={24} lg={12}>
          <div className="page-panel">
            <div className="page-header" style={{ marginBottom: 8 }}>
              <strong style={{ fontSize: 13 }}>Recent suppliers</strong>
            </div>
            {suppliers.length > 0 ? (
              suppliers.slice(0, 5).map((supplier) => (
                <div
                  key={supplier._id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "6px 0",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>
                      {supplier.name}
                    </div>
                    <small style={{ color: "#64748b" }}>
                      {supplier.contactPerson}
                    </small>
                  </div>
                  <Tag color={supplier.isActive ? "green" : "default"}>
                    {supplier.isActive ? "Active" : "Inactive"}
                  </Tag>
                </div>
              ))
            ) : (
              <p style={{ textAlign: "center", color: "#94a3b8", margin: "12px 0" }}>
                No suppliers found
              </p>
            )}
          </div>
        </Col>
      </Row>

      <div className="page-panel" style={{ marginTop: 10 }}>
        <div className="page-header" style={{ marginBottom: 8 }}>
          <strong style={{ fontSize: 13 }}>Quick actions</strong>
        </div>
        <Space size={8} wrap>
          <Button
            icon={<MedicineBoxOutlined />}
            onClick={() => navigate("/stock")}
          >
            Stock
          </Button>
          <Button icon={<TeamOutlined />} onClick={() => navigate("/suppliers")}>
            Suppliers
          </Button>
          <Button
            type="primary"
            icon={<FileTextOutlined />}
            onClick={() => navigate("/billing")}
          >
            Billing
          </Button>
          <Button icon={<UserOutlined />} onClick={() => navigate("/patients")}>
            Patients
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default Dashboard;
